import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CreditCard, Trash2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { paymentsAPI } from '../../lib/api';

const PaymentMethodsTab = ({
  paymentMethods = [],
  loading = false,
  onRefresh = () => {},
  settingDefaultCard = null,
  setSettingDefaultCard = () => {},
  deletingCard = null,
  setDeletingCard = () => {},
  addingCard = false,
  onAddCard = () => {}
}) => {
  const handleSetDefaultCard = async (cardId) => {
    if (setSettingDefaultCard) setSettingDefaultCard(cardId);
    try {
      await paymentsAPI.setDefaultCard(cardId);
      toast.success('Default card updated');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update default card');
    } finally {
      if (setSettingDefaultCard) setSettingDefaultCard(null);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!confirm('Are you sure you want to remove this card?')) return;
    
    if (setDeletingCard) setDeletingCard(cardId);
    try {
      await paymentsAPI.deleteCard(cardId);
      toast.success('Card removed');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove card');
    } finally {
      if (setDeletingCard) setDeletingCard(null);
    }
  };

  return (
    <Card className="bg-[#111] border-[#333]">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-400" />
              Billing Information
            </CardTitle>
            <p className="text-white/60 text-sm mt-1">
              You can add multiple cards and switch between them when making bookings.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onAddCard}
              disabled={addingCard}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="add-card-btn"
            >
              {addingCard ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Adding...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Add New
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : paymentMethods.length === 0 ? (
          <div className="text-center py-12">
            <CreditCard className="w-16 h-16 mx-auto mb-4 text-white/20" />
            <p className="text-white/50 mb-2 text-lg">No saved payment methods</p>
            <p className="text-white/40 text-sm mb-4">
              Cards are automatically saved when you complete an enrollment
            </p>
            <Button
              onClick={onAddCard}
              disabled={addingCard}
              className="bg-[#FF007F] hover:bg-[#D6006A]"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Add Your First Card
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Delete button at top */}
            {paymentMethods.some(card => card.is_default) && (
              <div className="border-b border-[#333] pb-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const defaultCard = paymentMethods.find(c => c.is_default);
                    if (defaultCard) handleDeleteCard(defaultCard.id);
                  }}
                  disabled={deletingCard !== null}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            )}
            
            {/* Cards grid */}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {paymentMethods.map((card) => (
                <div 
                  key={card.id}
                  className={`relative rounded-2xl p-6 min-h-[200px] ${
                    card.is_default 
                      ? 'bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-200' 
                      : 'bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300'
                  }`}
                  style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                >
                  {/* Card Header with Brand and Default Badge */}
                  <div className="flex items-start justify-between mb-8">
                    {/* Card Brand Logo */}
                    <div className="w-14 h-10 flex items-center justify-center">
                      {card.brand.toLowerCase() === 'mastercard' ? (
                        <div className="flex">
                          <div className="w-8 h-8 rounded-full bg-red-500 opacity-90"></div>
                          <div className="w-8 h-8 rounded-full bg-orange-400 opacity-90 -ml-4"></div>
                        </div>
                      ) : card.brand.toLowerCase() === 'visa' ? (
                        <span className="text-2xl font-bold italic text-blue-800">VISA</span>
                      ) : card.brand.toLowerCase() === 'amex' ? (
                        <span className="text-lg font-bold text-blue-600">AMEX</span>
                      ) : (
                        <span className="text-sm font-bold text-gray-700">{card.brand}</span>
                      )}
                    </div>
                    
                    {/* Default Badge */}
                    {card.is_default && (
                      <Badge className="bg-emerald-500 text-white border-0 px-3 py-1">
                        Default
                      </Badge>
                    )}
                  </div>
                  
                  {/* Card Number */}
                  <div className="mb-6">
                    <p className="text-gray-900 font-mono text-xl tracking-wider">
                      <span className="mr-3">****</span>
                      <span className="mr-3">****</span>
                      <span className="mr-3">****</span>
                      <span className="text-2xl font-bold">{card.last4}</span>
                    </p>
                  </div>
                  
                  {/* Expiry Date */}
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-gray-500 text-xs uppercase tracking-wider">Expiry</p>
                      <p className="text-gray-500 text-xs uppercase tracking-wider">Date</p>
                      <p className="text-gray-900 font-bold text-xl">
                        {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                      </p>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      {!card.is_default && (
                        <Button
                          size="sm"
                          onClick={() => handleSetDefaultCard(card.id)}
                          disabled={settingDefaultCard === card.id}
                          className="bg-blue-500 hover:bg-blue-600 text-white text-xs"
                          data-testid={`set-default-${card.last4}`}
                        >
                          {settingDefaultCard === card.id ? '...' : 'Set Default'}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteCard(card.id)}
                        disabled={deletingCard === card.id}
                        className="text-red-500 hover:bg-red-100 hover:text-red-600"
                        data-testid={`delete-card-${card.last4}`}
                      >
                        {deletingCard === card.id ? (
                          <div className="animate-spin w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="mt-6 p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-400 font-medium text-sm">About Saved Cards</p>
              <p className="text-amber-400/70 text-xs mt-1">
                Your card details are stored securely with Stripe and are used for term renewals 
                and recurring subscriptions. We never have direct access to your full card numbers.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentMethodsTab;
